import { useLocation } from "@tanstack/react-router";
import { ComplianceNotice } from "@/components/ComplianceNotice";

type Variant =
  | "default"
  | "journal"
  | "events"
  | "community"
  | "practice"
  | "assessment"
  | "therapists";

/**
 * Site-wide safety / scope notice.
 *
 * Placement and wording are identical on the website and inside the iOS app,
 * so every practice, journal, assessment, community and event surface carries
 * the same "support and education, not treatment" statement with a route to
 * crisis resources — instead of it living only on the legal pages.
 *
 * Pages that already render their own ComplianceNotice are excluded so the
 * notice never appears twice.
 */

const PRACTICE_ROUTES = [
  "/grounding",
  "/somatic",
  "/yoga",
  "/quick-resets",
  "/sleep",
  "/sleep-dreams",
  "/nervous-system-reset",
  "/relaxation-practices",
  "/safety-stabilisation",
  "/aromatherapy",
  "/forest-bathing",
  "/nature-connection",
  "/gardening",
  "/acts-of-self-care",
  "/self-love",
  "/inner-child",
  "/shadow-work",
  "/shadow-journey",
  "/affirmations",
  "/sounds",
  "/check-in",
  "/mood",
  "/how-are-you-feeling",
  "/cbt-thought-cards",
  "/dbt-skills",
  "/eft-flashcards",
  "/flashcards",
  "/creative-reflection",
  "/art-board",
  "/holistic-wellbeing",
  "/guide",
];

const JOURNAL_ROUTES = [
  "/journals",
  "/dissociation-journal",
  "/trigger-journal",
  "/abuse-journal",
  "/music-journal",
  "/guided-journal",
  "/know-your-patterns",
  "/my-healing-journey",
];

const ASSESSMENT_ROUTES = ["/quiz", "/learn/assessments", "/self-assessments"];

const THERAPIST_ROUTES = ["/therapist-directory", "/find-a-therapist"];

const COMMUNITY_ROUTES = ["/community", "/join-community"];

const EDUCATION_ROUTES = ["/learn", "/courses", "/library", "/workbooks", "/therapy-approaches"];

/** Pages that already show their own notice, or where one is inappropriate. */
const EXCLUDED = [
  "/",
  // The Recovery Board renders its own board-specific notice below the board.
  "/recovery-board",
  "/journal",
  "/dreams",
  "/breathing",
  "/tapping",
  "/meditations",
  "/bilateral",
  "/events",
  "/my-events",
  "/faq",
  "/support",
  "/crisis",
  "/disclaimer",
  "/ai-disclaimer",
  "/privacy",
  "/terms",
  "/cookies",
  "/community-guidelines",
  "/admin",
  "/login",
  "/signup",
  "/auth",
  "/checkout",
  "/choose-membership",
  "/complete-membership",
];

function matches(list: string[], pathname: string) {
  return list.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

function variantFor(pathname: string): Variant | null {
  if (matches(EXCLUDED, pathname)) return null;
  if (matches(JOURNAL_ROUTES, pathname)) return "journal";
  if (matches(ASSESSMENT_ROUTES, pathname)) return "assessment";
  if (matches(THERAPIST_ROUTES, pathname)) return "therapists";
  if (matches(COMMUNITY_ROUTES, pathname)) return "community";
  if (matches(PRACTICE_ROUTES, pathname)) return "practice";
  if (matches(EDUCATION_ROUTES, pathname)) return "default";
  return null;
}

export function PageSafetyNotice() {
  const { pathname } = useLocation();
  const variant = variantFor(pathname);
  if (!variant) return null;

  return (
    <div className="mx-auto w-full max-w-3xl px-4 pb-2 pt-4">
      <ComplianceNotice variant={variant} />
    </div>
  );
}

export default PageSafetyNotice;
